import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid, Alert, Snackbar,
  CircularProgress, Skeleton, InputAdornment, Chip, Tooltip, Autocomplete,
  Divider,
} from '@mui/material';
import { Search as SearchIcon, Eye, RefreshCw, ArrowRightLeft } from 'lucide-react';
import { getStockTransfers, createStockTransfer, generateTransferNumber } from '../firebase/stockTransferService';
import { getGRNs } from '../firebase/grnService';
import { logActivity, ACTIONS, ENTITY_TYPES } from '../firebase/activityLogService';

const StockTransfer = ({ currentUser }) => {
  const [transfers, setTransfers] = useState([]);
  const [grns, setGRNs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [viewTransfer, setViewTransfer] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Form state
  const [transferNumber, setTransferNumber] = useState('');
  const [selectedGRN, setSelectedGRN] = useState(null);
  const [transferDate, setTransferDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [transferItems, setTransferItems] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    const [tResult, gResult] = await Promise.all([getStockTransfers(), getGRNs()]);
    if (tResult.success) setTransfers(tResult.data);
    if (gResult.success) setGRNs(gResult.data);
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    showSnackbar('Refreshed');
  };

  const openCreate = () => {
    setTransferNumber('');
    setSelectedGRN(null);
    setTransferDate(new Date().toISOString().split('T')[0]);
    setNotes('');
    setTransferItems([]);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedGRN(null);
    setTransferItems([]);
  };

  // When a GRN is selected, populate transfer items from its available store stock
  const handleGRNSelect = (grn) => {
    setSelectedGRN(grn);
    if (!grn) { setTransferItems([]); return; }

    const availableItems = grn.items
      .filter((item) => (item.storeQty ?? item.qtyReceived) > 0)
      .map((item) => ({
        itemIndex: item.itemIndex,
        name: item.name,
        category: item.category,
        itemCode: item.itemCode || '',
        available: item.storeQty ?? item.qtyReceived,
        qty: 0,
        costPrice: item.costPerUnit,
        retailPrice: item.retailPrice || '',
        wholesalePrice: item.wholesalePrice || '',
        isInternal: !!item.isInternal,
      }));

    setTransferItems(availableItems);
  };

  const updateTransferItem = (index, field, value) => {
    setTransferItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleCreate = async () => {
    if (submitting) return;

    if (!selectedGRN) {
      showSnackbar('Please select a GRN', 'error');
      return;
    }

    const itemsToTransfer = transferItems.filter((i) => parseFloat(i.qty) > 0);
    if (itemsToTransfer.length === 0) {
      showSnackbar('Enter quantity for at least one item', 'error');
      return;
    }

    const overQty = itemsToTransfer.find((i) => parseFloat(i.qty) > i.available);
    if (overQty) {
      showSnackbar(`Quantity for "${overQty.name}" exceeds available store stock (${overQty.available})`, 'error');
      return;
    }

    const missingPrices = itemsToTransfer.find((i) => !i.isInternal && (!i.retailPrice || !i.wholesalePrice));
    if (missingPrices) {
      showSnackbar(`Set retail and wholesale prices for "${missingPrices.name}"`, 'error');
      return;
    }

    setSubmitting(true);
    try {
      // Generate number only at save time
      const { transferNumber: tNum } = await generateTransferNumber();
      const finalTransferNumber = tNum;

      const result = await createStockTransfer({
        transferNumber: finalTransferNumber,
        grnId: selectedGRN.id,
        grnNumber: selectedGRN.grnNumber,
        supplier: selectedGRN.supplier,
        transferDate,
        notes,
        createdBy: currentUser?.email || '',
        items: itemsToTransfer.map((i) => ({
          itemIndex: i.itemIndex,
          name: i.name,
          category: i.category,
          itemCode: i.itemCode || '',
          qty: parseFloat(i.qty),
          costPrice: parseFloat(i.costPrice) || 0,
          retailPrice: parseFloat(i.retailPrice) || 0,
          wholesalePrice: parseFloat(i.wholesalePrice) || 0,
          isInternal: !!i.isInternal,
        })),
      });

      if (result.success) {
        // Update local state — no need to re-fetch everything from Firestore
        const newTransfer = {
          id: result.id,
          transferNumber: result.transferNumber,
          grnId: selectedGRN.id,
          grnNumber: selectedGRN.grnNumber,
          supplier: selectedGRN.supplier,
          transferDate,
          notes,
          items: result.processedItems,
          totalQty: result.totalQty,
          totalValue: result.totalValue,
          createdAt: new Date().toISOString(),
        };
        setTransfers((prev) => [newTransfer, ...prev]);

        // Update the GRN status in local state
        setGRNs((prev) => prev.map((g) => {
          if (g.id !== selectedGRN.id) return g;
          const updatedItems = g.items.map((item) => {
            const transferred = itemsToTransfer.find((t) => t.itemIndex === item.itemIndex);
            if (!transferred) return item;
            const newStoreQty = Math.max(0, (item.storeQty ?? item.qtyReceived) - parseFloat(transferred.qty));
            return { ...item, storeQty: newStoreQty, transferredQty: (item.transferredQty || 0) + parseFloat(transferred.qty) };
          });
          const allDone = updatedItems.every((i) => (i.storeQty ?? 0) === 0);
          const anyDone = updatedItems.some((i) => (i.transferredQty || 0) > 0);
          return { ...g, items: updatedItems, status: allDone ? 'Completed' : anyDone ? 'Partial' : 'Received' };
        }));

        closeModal();
        showSnackbar(`Transfer ${result.transferNumber} completed — stock is now available for billing`);
        logActivity({
          userId: currentUser?.uid, userEmail: currentUser?.email,
          userName: currentUser?.displayName || currentUser?.email,
          action: ACTIONS.CREATED_STOCK_TRANSFER,
          entityType: ENTITY_TYPES.STOCK_TRANSFER,
          entityId: result.id, entityName: `Transfer #${result.transferNumber}`,
          details: {
            grnNumber: selectedGRN.grnNumber,
            supplier: selectedGRN.supplier,
            itemCount: itemsToTransfer.length,
            totalQty: itemsToTransfer.reduce((s, i) => s + parseFloat(i.qty), 0),
          },
        });
      } else {
        showSnackbar(result.error || 'Transfer failed', 'error');
      }
    } catch (err) {
      showSnackbar('Unexpected error during transfer', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = transfers.filter(
    (t) =>
      t.transferNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.grnNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.supplier || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // GRNs that still have store stock available
  const availableGRNs = grns.filter((g) =>
    g.status !== 'Completed' &&
    g.items?.some((i) => (i.storeQty ?? i.qtyReceived) > 0)
  );

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={300} height={50} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" width="100%" height={400} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Stock Transfer
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Transfer stock from store to shop. Creates FIFO batches for billing.
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6" fontWeight={600}>All Transfers</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" size="small"
                startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshCw size={16} />}
                onClick={handleRefresh} disabled={refreshing}>
                Refresh
              </Button>
              <Button variant="contained" size="small" startIcon={<ArrowRightLeft size={16} />} onClick={openCreate}>
                New Transfer
              </Button>
            </Box>
          </Box>

          <TextField fullWidth placeholder="Search by transfer #, GRN # or supplier..."
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon size={18} /></InputAdornment> }}
          />

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Transfer #</strong></TableCell>
                  <TableCell><strong>GRN #</strong></TableCell>
                  <TableCell><strong>Supplier</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell align="center"><strong>Items</strong></TableCell>
                  <TableCell align="right"><strong>Total Qty</strong></TableCell>
                  <TableCell align="right"><strong>Total Value</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell>
                      <Typography variant="body2" color="primary" fontWeight={600}>
                        {t.transferNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={t.grnNumber} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{t.supplier}</TableCell>
                    <TableCell>{t.transferDate}</TableCell>
                    <TableCell align="center">{t.items?.length || 0}</TableCell>
                    <TableCell align="right">{t.totalQty || 0}</TableCell>
                    <TableCell align="right">
                      LKR {(t.totalValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View details">
                        <IconButton size="small" color="primary" onClick={() => setViewTransfer(t)}>
                          <Eye size={17} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <ArrowRightLeft size={40} style={{ opacity: 0.3, marginBottom: 8 }} />
                      <Typography color="text.secondary">No transfers found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create Transfer Dialog */}
      <Dialog open={showModal} onClose={closeModal} maxWidth="lg" fullWidth>
        <DialogTitle>New Stock Transfer</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Transfer Number" value={transferNumber || ''}
                  InputProps={{ readOnly: true }}
                  placeholder="Auto-generated on save"
                  helperText="Auto-generated on save" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={availableGRNs}
                  getOptionLabel={(g) => `${g.grnNumber} — ${g.supplier}`}
                  value={selectedGRN}
                  onChange={(_, val) => handleGRNSelect(val)}
                  renderInput={(params) => (
                    <TextField {...params} label="Source GRN *"
                      placeholder="Select GRN with available stock" />
                  )}
                  noOptionsText="No GRNs with available store stock"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField 
                  fullWidth 
                  label="Transfer Date" 
                  type="date" 
                  value={transferDate}
                  onChange={(e) => setTransferDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Notes" value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional notes" multiline rows={2} />
              </Grid>
            </Grid>

            {selectedGRN && (
              <>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  Items — set transfer quantity and selling prices
                </Typography>

                {transferItems.length === 0 ? (
                  <Alert severity="warning">This GRN has no available store stock to transfer.</Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Item</strong></TableCell>
                          <TableCell><strong>Category</strong></TableCell>
                          <TableCell align="right"><strong>Available</strong></TableCell>
                          <TableCell align="right"><strong>Transfer Qty *</strong></TableCell>
                          <TableCell align="right"><strong>Cost/Unit</strong></TableCell>
                          <TableCell align="right"><strong>Retail Price (LKR)</strong></TableCell>
                          <TableCell align="right"><strong>Wholesale Price (LKR)</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {transferItems.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              {item.name}
                              {item.isInternal && (
                                <Chip label="Internal" size="small" color="warning" variant="outlined" sx={{ ml: 1, fontSize: '0.65rem', height: 18 }} />
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip label={item.category} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell align="right">
                              <Chip label={item.available} size="small" color="info" />
                            </TableCell>
                            <TableCell align="right">
                              <TextField type="number" size="small" value={item.qty}
                                onChange={(e) => updateTransferItem(index, 'qty', e.target.value)}
                                sx={{ width: 80 }}
                                inputProps={{ min: 0, max: item.available }}
                                error={parseFloat(item.qty) > item.available}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="text.secondary">
                                {parseFloat(item.costPrice).toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {item.isInternal ? (
                                <Typography variant="caption" color="text.disabled">N/A (Internal)</Typography>
                              ) : (
                                <TextField type="number" size="small" value={item.retailPrice}
                                  onChange={(e) => updateTransferItem(index, 'retailPrice', e.target.value)}
                                  sx={{ width: 100 }} inputProps={{ min: 0, step: 0.01 }} />
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {item.isInternal ? (
                                <Typography variant="caption" color="text.disabled">N/A (Internal)</Typography>
                              ) : (
                                <TextField type="number" size="small" value={item.wholesalePrice}
                                  onChange={(e) => updateTransferItem(index, 'wholesalePrice', e.target.value)}
                                  sx={{ width: 100 }} inputProps={{ min: 0, step: 0.01 }} />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                <Alert severity="info" sx={{ mt: 2 }}>
                  Transferred items will be added as FIFO batches in inventory and become immediately available for billing.
                </Alert>
              </>
            )}

            {!selectedGRN && (
              <Alert severity="info">Select a GRN above to see available store stock.</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeModal} disabled={submitting}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={submitting || !selectedGRN}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <ArrowRightLeft size={16} />}>
            {submitting ? 'Transferring...' : 'Confirm Transfer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Transfer Dialog */}
      <Dialog open={!!viewTransfer} onClose={() => setViewTransfer(null)} maxWidth="md" fullWidth>
        <DialogTitle>Transfer Details — {viewTransfer?.transferNumber}</DialogTitle>
        <DialogContent>
          {viewTransfer && (
            <Box sx={{ pt: 1 }}>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Transfer #</Typography>
                  <Typography fontWeight={600}>{viewTransfer.transferNumber}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Source GRN</Typography>
                  <Typography fontWeight={600}>{viewTransfer.grnNumber}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Supplier</Typography>
                  <Typography>{viewTransfer.supplier}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Date</Typography>
                  <Typography>{viewTransfer.transferDate}</Typography>
                </Grid>
              </Grid>
              {viewTransfer.notes && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Notes: {viewTransfer.notes}
                </Typography>
              )}
              <Divider sx={{ mb: 2 }} />
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Item</strong></TableCell>
                      <TableCell><strong>Category</strong></TableCell>
                      <TableCell align="right"><strong>Qty</strong></TableCell>
                      <TableCell align="right"><strong>Cost</strong></TableCell>
                      <TableCell align="right"><strong>Retail</strong></TableCell>
                      <TableCell align="right"><strong>Wholesale</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {viewTransfer.items?.map((item, i) => (
                      <TableRow key={i}>
                        <TableCell>{item.name}</TableCell>
                        <TableCell>{item.category}</TableCell>
                        <TableCell align="right">{item.qty}</TableCell>
                        <TableCell align="right">{parseFloat(item.costPrice).toFixed(2)}</TableCell>
                        <TableCell align="right">{parseFloat(item.retailPrice).toFixed(2)}</TableCell>
                        <TableCell align="right">{parseFloat(item.wholesalePrice).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 2, display: 'flex', gap: 3, justifyContent: 'flex-end' }}>
                <Typography variant="body2" color="text.secondary">
                  Total Qty: <strong>{viewTransfer.totalQty}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Value: <strong>LKR {(viewTransfer.totalValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setViewTransfer(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StockTransfer;
